/**
 * Painel de tela: o cartão da Kumo com título, descrição e entrada animada.
 *
 * Substitui o `Card` do antd. A descrição fica logo abaixo do título, e não num
 * parágrafo solto: em todo painel ela diz por que aquele número importa, e é a primeira
 * coisa que precisa ser lida.
 */

import type { ReactNode } from "react";
import { LayerCard } from "@cloudflare/kumo";
import { motion, type Variants } from "motion/react";

/**
 * Entrada de cada painel: sobe e aparece.
 *
 * Os nomes `hidden` e `visible` são herdados do contêiner da tela (`Page`), que dispara
 * os painéis em cascata — o olho acompanha a ordem de leitura em vez de receber a tela
 * inteira de uma vez.
 */
export const PANEL_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

/** Entradas do painel. */
interface PanelProps {
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  /** Ações ou controles alinhados à direita do título. */
  readonly extra?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly bodyClassName?: string;
}

/** Cartão de tela, com cabeçalho opcional e entrada suave. */
export function Panel({
  title,
  description,
  extra,
  children,
  className = "",
  bodyClassName = "",
}: PanelProps): React.ReactElement {
  const hasHeader = title !== undefined || extra !== undefined;
  return (
    <motion.section variants={PANEL_VARIANTS} className={`min-w-0 ${className}`}>
      <LayerCard className="h-full">
        {hasHeader && (
          <LayerCard.Secondary className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              {title !== undefined && (
                <h2 className="text-base font-semibold text-kumo-strong">{title}</h2>
              )}
              {description !== undefined && (
                <p className="mt-1 max-w-3xl text-sm text-kumo-subtle">{description}</p>
              )}
            </div>
            {extra}
          </LayerCard.Secondary>
        )}
        <LayerCard.Primary className={bodyClassName}>{children}</LayerCard.Primary>
      </LayerCard>
    </motion.section>
  );
}
